import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { get_dp_detail } from "../../utils/api";
import {
  Box,
  Card,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  VStack,
  Icon,
  Badge,
  Divider,
  Skeleton,
  useDisclosure,
} from "@chakra-ui/react";
import CustomButton from "../../components/ButtonComp";
import { BiArrowBack } from "react-icons/bi";
import ImageModal from "../../components/ImageModal";
import {
  FiUser,
  FiBriefcase,
  FiMapPin,
  FiPhone,
  FiMail,
  FiFileText,
  FiCreditCard,
  FiEye,
} from "react-icons/fi";

// ── Reusable: section header ──────────────────────────────────────────────────
const SectionHeader = ({ icon, title, color = "blue.500", count }) => (
  <Flex align="center" gap={3} mb={4}>
    <Box
      w="34px"
      h="34px"
      borderRadius="9px"
      bg={`${color.split(".")[0]}.50`}
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <Icon as={icon} color={color} boxSize={4} />
    </Box>
    <Flex align="center" gap={2}>
      <Text fontWeight="700" fontSize="md" color="gray.700">
        {title}
      </Text>
      {count != null && (
        <Badge colorScheme="blue" borderRadius="8px" px={2} fontSize="xs">
          {count}
        </Badge>
      )}
    </Flex>
  </Flex>
);

// ── Reusable: info field ──────────────────────────────────────────────────────
const InfoField = ({ label, value }) => (
  <Box>
    <Text
      fontSize="10px"
      fontWeight="700"
      color="gray.400"
      textTransform="uppercase"
      letterSpacing="0.6px"
      mb="3px"
    >
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="500" color="gray.800" wordBreak="break-word">
      {value || "N/A"}
    </Text>
  </Box>
);

// ── Main ──────────────────────────────────────────────────────────────────────
const DPdetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const { onOpen, onClose, isOpen } = useDisclosure();
  const [url, setUrl] = useState("");

  useEffect(() => {
    get_dp_detail(location.state, setLoading).then((el) => {
      if (el.success) setData(el.reponselist);
    });
  }, []);

  return (
    <>
      {/* ── Top Bar ──────────────────────────────────────────────────────── */}
      <Card
        display="flex"
        flexDirection="row"
        alignItems="center"
        justify="space-between"
        paddingInline={5}
        paddingBlock={3}
        boxShadow="sm"
        borderRadius="12px"
        mb={1}
      >
        <Flex align="center" gap={3}>
          <Box
            w="36px"
            h="36px"
            borderRadius="9px"
            bg="blue.50"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FiUser} color="blue.500" boxSize={4} />
          </Box>
          <Box>
            <Heading fontSize="lg" color="gray.800">
              DP Details
            </Heading>
            {data.uuid && (
              <Text fontSize="xs" color="gray.400">
                ID: {data.uuid}
              </Text>
            )}
          </Box>
        </Flex>
        <CustomButton
          icon={<BiArrowBack color="#fff" />}
          title="Back"
          height="34px"
          b_color="#374151"
          onClick={() =>
            navigate("/dp-list", {
              state: { page: location.state?.page, rows: location.state?.rows },
            })
          }
        />
      </Card>

      {/* ── Loading ───────────────────────────────────────────────────────── */}
      {loading ? (
        <VStack spacing={4} align="stretch" mt={4}>
          <Card borderRadius="14px" p={6}>
            <Skeleton h="14px" w="30%" mb={5} borderRadius="8px" />
            <SimpleGrid columns={[1, 2, 3]} spacing={5}>
              {[...Array(9)].map((_, i) => (
                <Box key={i}>
                  <Skeleton h="10px" mb={2} w="40%" borderRadius="6px" />
                  <Skeleton h="14px" w="70%" borderRadius="6px" />
                </Box>
              ))}
            </SimpleGrid>
          </Card>
          <Card borderRadius="14px" p={6}>
            <Skeleton h="14px" w="25%" mb={4} borderRadius="8px" />
            <Skeleton h="80px" borderRadius="10px" />
          </Card>
        </VStack>
      ) : (
        <VStack spacing={4} align="stretch" mt={4}>

          {/* ── Basic Info ───────────────────────────────────────────────── */}
          <Card
            bg="white"
            borderRadius="14px"
            boxShadow="0 2px 8px rgba(0,0,0,0.06)"
            border="1.5px solid"
            borderColor="gray.100"
            p={6}
          >
            {/* ID banner */}
            {data.uuid && (
              <Flex align="center" gap={2} mb={5}>
                <Badge colorScheme="orange" px={3} py={1} borderRadius="8px" fontSize="sm" fontWeight="700">
                  {data.uuid}
                </Badge>
              </Flex>
            )}

            <SectionHeader icon={FiUser} title="General Information" color="blue.500" />
            <SimpleGrid columns={[1, 2, 3]} spacing={5} mb={5}>
              <InfoField label="Name" value={data.name} />
              <InfoField label="Trade Name" value={data.tradename} />
              <InfoField label="Legal Name" value={data.legalname} />
              <InfoField label="GST Number" value={data.gst} />
              <InfoField label="PAN Number" value={data.pan} />
            </SimpleGrid>

            <Divider mb={5} />

            <SectionHeader icon={FiPhone} title="Contact Information" color="green.500" />
            <SimpleGrid columns={[1, 2, 3]} spacing={5} mb={5}>
              <InfoField label="Mobile 1" value={data.mobile1} />
              <InfoField label="Mobile 2" value={data.mobile2} />
              <InfoField label="Email 1" value={data.email1} />
              <InfoField label="Email 2" value={data.email2} />
            </SimpleGrid>

            <Divider mb={5} />

            <SectionHeader icon={FiMapPin} title="Address Information" color="orange.500" />
            <SimpleGrid columns={[1, 2, 3]} spacing={5}>
              <InfoField label="Address" value={data.address} />
              <InfoField label="Address 1" value={data.address1} />
              <InfoField label="Address 2" value={data.address2} />
              <InfoField label="District" value={data.district} />
              <InfoField label="State" value={data.state} />
              <InfoField label="Pincode" value={data.pincode} />
            </SimpleGrid>
          </Card>

          {/* ── Documents ────────────────────────────────────────────────── */}
          <Card
            bg="white"
            borderRadius="14px"
            boxShadow="0 2px 8px rgba(0,0,0,0.06)"
            border="1.5px solid"
            borderColor="gray.100"
            p={6}
          >
            <SectionHeader
              icon={FiFileText}
              title="Document Details"
              color="purple.500"
              count={data?.documents?.length}
            />

            {!data?.documents || data.documents.length === 0 ? (
              <Box
                bg="gray.50"
                borderRadius="10px"
                p={6}
                textAlign="center"
                border="1.5px dashed"
                borderColor="gray.200"
              >
                <Text fontSize="sm" color="gray.400">
                  No documents available
                </Text>
              </Box>
            ) : (
              <SimpleGrid columns={[1, 2]} spacing={3}>
                {data.documents.map((el, i) => (
                  <Box
                    key={i}
                    bg="gray.50"
                    borderRadius="10px"
                    border="1.5px solid"
                    borderColor="gray.100"
                    p={4}
                  >
                    <Flex justify="space-between" align="flex-start" mb={3}>
                      <Box flex={1} mr={3}>
                        <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb="2px">
                          Document Name
                        </Text>
                        <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>
                          {el.name || "N/A"}
                        </Text>
                      </Box>
                      <CustomButton
                        title="View"
                        height="28px"
                        onClick={() => { setUrl(el.url); onOpen(); }}
                      />
                    </Flex>
                    <Box>
                      <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb="2px">
                        Doc Type
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {el.doc_name || "N/A"}
                      </Text>
                    </Box>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </Card>

          {/* ── Bank Details ──────────────────────────────────────────────── */}
          <Card
            bg="white"
            borderRadius="14px"
            boxShadow="0 2px 8px rgba(0,0,0,0.06)"
            border="1.5px solid"
            borderColor="gray.100"
            p={6}
          >
            <SectionHeader
              icon={FiCreditCard}
              title="Bank Details"
              color="teal.500"
              count={data?.banklist?.length}
            />

            {!data?.banklist || data.banklist.length === 0 ? (
              <Box
                bg="gray.50"
                borderRadius="10px"
                p={6}
                textAlign="center"
                border="1.5px dashed"
                borderColor="gray.200"
              >
                <Text fontSize="sm" color="gray.400">
                  No bank accounts available
                </Text>
              </Box>
            ) : (
              <SimpleGrid columns={[1, 2]} spacing={3}>
                {data.banklist.map((el, i) => (
                  <Box
                    key={i}
                    bg="gray.50"
                    borderRadius="10px"
                    border="1.5px solid"
                    borderColor="gray.100"
                    p={4}
                  >
                    <Flex justify="space-between" align="flex-start" mb={3}>
                      <Box flex={1} mr={3}>
                        <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb="2px">
                          Account Holder
                        </Text>
                        <Text fontSize="sm" fontWeight="600" color="gray.800">
                          {el.name || "N/A"}
                        </Text>
                      </Box>
                      {el.url && (
                        <CustomButton
                          title="View"
                          height="28px"
                          onClick={() => { setUrl(el.url); onOpen(); }}
                        />
                      )}
                    </Flex>
                    <SimpleGrid columns={2} spacing={3}>
                      <InfoField label="Bank Name" value={el.bankname} />
                      <InfoField label="Account No." value={el.account_no} />
                      <InfoField label="Branch" value={el.branch_name} />
                      <InfoField label="IFSC Code" value={el.ifsc_code} />
                    </SimpleGrid>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </Card>

        </VStack>
      )}

      <ImageModal url={url} onClose={onClose} isOpen={isOpen} />
    </>
  );
};

export default DPdetail;
